import { useParams } from 'react-router-dom'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import ProjectLayout from '@/components/ProjectLayout'
import { projectMemberService, type ProjectMember } from '@/services/projectMemberService'
import { projectService, type Project } from '@/services/projectService'
import { HttpError } from '@/lib/httpError'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import ErrorDisplay from '@/components/ui/ErrorDisplay'
import CustomizableTable, { type TableColumn } from '@/components/ui/CustomizableTable'
import AddProjectMemberModal, { type AddProjectMemberModalRef } from '@/components/modals/AddProjectMemberModal'
import ProjectMemberDetailsModal, { type ProjectMemberDetailsModalRef } from '@/components/modals/ProjectMemberDetailsModal'
import ChangeProjectMemberRoleModal, { type ChangeProjectMemberRoleModalRef } from '@/components/modals/ChangeProjectMemberRoleModal'
import DeleteProjectMemberModal, { type DeleteProjectMemberModalRef } from '@/components/modals/DeleteProjectMemberModal'

export default function ProjectMembersPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { t, i18n } = useTranslation('projectMembers')
  const { user: currentUser, refreshAuth } = useAuth()
  const [members, setMembers] = useState<ProjectMember[]>([])
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)

  // Refs to track latest values
  const currentPageRef = useRef(currentPage)
  const addMemberModalRef = useRef<AddProjectMemberModalRef>(null)
  const memberDetailsModalRef = useRef<ProjectMemberDetailsModalRef>(null)
  const changeRoleModalRef = useRef<ChangeProjectMemberRoleModalRef>(null)
  const deleteMemberModalRef = useRef<DeleteProjectMemberModalRef>(null)

  useEffect(() => {
    currentPageRef.current = currentPage
  }, [currentPage])

  // Check permissions based on platform_role and project role
  const isPlatformOwner = currentUser?.platform_role === 'owner'
  const isProjectOwner = project?.role === 'owner'
  const isProjectAdmin = project?.role === 'admin'
  const canManageMembers = isPlatformOwner || isProjectOwner || isProjectAdmin

  // Permissions for adding members
  const canDirectAdd = isPlatformOwner || isProjectOwner
  const canRequestAdd = isPlatformOwner || isProjectOwner || isProjectAdmin

  // Fetch project info to get current user's role in this project
  const fetchProjectInfo = useCallback(async () => {
    if (!projectId) return

    try {
      const data = await projectService.getProjectById(projectId)
      setProject(data)
    } catch (err) {
      // Handle 401 errors with automatic token refresh
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          const data = await projectService.getProjectById(projectId)
          setProject(data)
        } catch (refreshError) {
          console.error('Failed to fetch project info:', refreshError)
        }
      }
    }
  }, [projectId, refreshAuth])

  useEffect(() => {
    fetchProjectInfo()
  }, [fetchProjectInfo])

  const fetchMembers = useCallback(async (page?: number) => {
    if (!projectId) return

    setLoading(true)
    setError('')

    const actualPage = page ?? currentPageRef.current

    // Ensure minimum 2 seconds loading time for better UX
    const startTime = Date.now()

    try {
      const offset = (actualPage - 1) * pageSize
      const response = await projectMemberService.getProjectMembers(projectId, offset, pageSize)

      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }

      setMembers(response.members || [])
      setTotalItems(response.total || 0)
      setCurrentPage(actualPage)
    } catch (err) {
      // Handle 401 errors with automatic token refresh
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          // Retry the fetch after token refresh
          return fetchMembers(page)
        } catch (refreshError) {
          // If refresh fails, show error
          const elapsed = Date.now() - startTime
          const remainingTime = Math.max(0, 2000 - elapsed)
          if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime))
          }
          setError(t('errors.operationFailed', 'Operation failed, please try again'))
          setLoading(false)
          return
        }
      }

      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }

      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, errorMessage))
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }, [projectId, pageSize, t, refreshAuth])

  useEffect(() => {
    fetchMembers(1)
  }, [fetchMembers])

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'admin':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200'
      case 'auditor':
        return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'security':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'member':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'viewer':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'observer':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      case 'user':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'local':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'ldap':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'oidc':
        return 'bg-green-100 text-green-800 border-green-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      const locale = i18n.language === 'zh' ? 'zh-TW' : 'en-US'

      return date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    } catch {
      return dateString
    }
  }

  // Define table columns
  const columns: TableColumn<ProjectMember>[] = [
    {
      id: 'account',
      label: t('table.account', 'Account'),
      accessor: (member) => member.user.account,
      sortable: true,
      searchable: true,
      alwaysVisible: true,
      render: (_, member) => (
        <div className="text-sm font-semibold text-gray-900">{member.user.account}</div>
      )
    },
    {
      id: 'name',
      label: t('table.name', 'Name'),
      accessor: (member) => member.user.name,
      sortable: true,
      searchable: true,
      render: (_, member) => (
        <div className="text-sm text-gray-900">{member.user.name}</div>
      )
    },
    {
      id: 'project_role',
      label: t('table.projectRole', 'Project Role'),
      accessor: 'role',
      sortable: true,
      filterable: true,
      filterOptions: ['owner', 'admin', 'member', 'viewer', 'observer'],
      render: (value) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getRoleColor(value)}`}>
          {String(t(`projectRoles.${value}`, value))}
        </span>
      )
    },
    {
      id: 'type',
      label: t('table.type', 'Type'),
      accessor: (member) => member.user.type,
      sortable: true,
      filterable: true,
      filterOptions: ['local', 'ldap', 'oidc'],
      render: (_, member) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getTypeColor(member.user.type)}`}>
          {String(t(`types.${member.user.type}`, member.user.type))}
        </span>
      )
    },
    {
      id: 'created_at',
      label: t('table.joinedAt', 'Joined At'),
      accessor: 'created_at',
      sortable: true,
      defaultVisible: false,
      sortValue: (member) => new Date(member.created_at).getTime(),
      render: (value) => (
        <div className="text-sm text-gray-600">{formatDate(value)}</div>
      )
    },
    {
      id: 'actions',
      label: t('table.actions', 'Actions'),
      accessor: () => null,
      alwaysVisible: true,
      render: (_, member) => {
        const isSelf = member.user_id === currentUser?.id

        // Permissions:
        // - view: everyone can view
        // - change-role: platform owner or project owner or project admin (but not on self)
        // - delete: platform owner or project owner or project admin (but not on self and not owner role)
        const canView = true
        const canChangeRole = canManageMembers && !isSelf
        const canDelete = canManageMembers && !isSelf && member.role !== 'owner'

        return (
          <div className="flex items-center justify-end gap-1">
            {/* View button - everyone can see */}
            <button
              onClick={() => memberDetailsModalRef.current?.open(member)}
              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
              title={t('actions.view', 'View')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>

            {/* Change Role button */}
            {canChangeRole && (
              <button
                onClick={() => changeRoleModalRef.current?.open(member)}
                className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                title={t('actions.changeRole', 'Change Role')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
            )}

            {/* Delete button */}
            {canDelete && (
              <button
                onClick={() => deleteMemberModalRef.current?.open(member)}
                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                title={t('actions.delete', 'Delete')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        )
      }
    }
  ]

  if (loading && !error) {
    return (
      <ProjectLayout>
        <LoadingOverlay message={t('loading', 'Loading project members...')} fullScreen />
      </ProjectLayout>
    )
  }

  if (error && !loading) {
    return (
      <ProjectLayout>
        <ErrorDisplay
          error={error}
          title={t('errors.title', 'Error')}
          onRefresh={() => fetchMembers()}
          fullScreen
        />
      </ProjectLayout>
    )
  }

  return (
    <ProjectLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-text-dark">{t('title', 'Project Members')}</h1>
              <p className="text-gray-600 mt-2">{t('description', 'Manage project team members and permissions')}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchMembers()}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{t('actions.refresh', 'Refresh')}</span>
              </button>
              {(canDirectAdd || canRequestAdd) && (
                <button
                  onClick={() => addMemberModalRef.current?.open()}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>{t('actions.addMember', 'Add Member')}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content - CustomizableTable */}
        {!error && (
          <CustomizableTable
            data={members}
            columns={columns}
            loading={loading}
            emptyMessage={t('empty.description', 'No members found in this project')}
            pagination={{
              currentPage,
              totalItems,
              pageSize,
              onPageChange: fetchMembers
            }}
          />
        )}

        {/* Modals */}
        <AddProjectMemberModal
          ref={addMemberModalRef}
          canDirectAdd={canDirectAdd}
          canRequestAdd={canRequestAdd}
          onSuccess={() => fetchMembers()}
        />
        <ProjectMemberDetailsModal
          ref={memberDetailsModalRef}
        />
        <ChangeProjectMemberRoleModal
          ref={changeRoleModalRef}
          canDirectChange={isPlatformOwner || isProjectOwner}
          canRequestChange={canManageMembers}
          onSuccess={() => fetchMembers()}
        />
        <DeleteProjectMemberModal
          ref={deleteMemberModalRef}
          canDirectDelete={isPlatformOwner || isProjectOwner}
          canRequestDelete={canManageMembers}
          onSuccess={() => fetchMembers()}
        />
      </div>
    </ProjectLayout>
  )
}
